import React, { useState, useMemo } from 'react';
import { Check, ChevronsUpDown, Search, User as UserIcon } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { cn } from '../../lib/utils';

/**
 * Searchable tenant picker.
 *  - type to filter by tenant name, room number, or phone
 *  - shows room chip + rent next to each option
 *  - selected state is persisted via `value` (tenant id)
 */
const TenantCombobox = ({
  tenants = [],
  value = '',
  onChange,
  placeholder = 'Search by name, room, or phone…',
  disabled = false,
  required = false,
  loading = false,
  testId = 'tenant-combobox',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const selected = useMemo(
    () => tenants.find((t) => t.id === value) || null,
    [tenants, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return tenants;
    return tenants.filter((t) => {
      const room = t.room_number && t.room_number !== 'N/A' ? String(t.room_number) : '';
      return [t.name, t.phone, t.email, room, `room ${room}`]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(q));
    });
  }, [tenants, query]);

  return (
    <Popover
      open={open}
      onOpenChange={(o) => { setOpen(o); if (!o) setQuery(''); }}
      modal={true}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-required={required}
          disabled={disabled}
          data-testid={testId}
          className={cn(
            'w-full flex items-center justify-between px-3 py-2 text-sm',
            'border border-gray-300 rounded-lg bg-white',
            'hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            !selected && 'text-gray-400',
          )}
        >
          {selected ? (
            <span className="flex items-center gap-2 min-w-0">
              <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[11px] font-semibold flex-shrink-0">
                {(selected.name || '?').charAt(0).toUpperCase()}
              </span>
              <span className="truncate font-medium text-gray-900">{selected.name}</span>
              {selected.room_number && selected.room_number !== 'N/A' && (
                <span className="px-1.5 py-0.5 text-[11px] bg-gray-100 text-gray-700 rounded font-medium flex-shrink-0">
                  Room {selected.room_number}
                </span>
              )}
              {selected.rent_amount ? (
                <span className="text-xs text-gray-500 flex-shrink-0">₹{Number(selected.rent_amount).toLocaleString()}</span>
              ) : null}
            </span>
          ) : (
            <span className="flex items-center gap-2 text-gray-400">
              <UserIcon size={14} /> {placeholder}
            </span>
          )}
          <ChevronsUpDown size={14} className="text-gray-400 flex-shrink-0 ml-2" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0 max-w-none"
        align="start"
        sideOffset={4}
      >
        <div className="flex items-center border-b px-3 h-11 gap-2">
          <Search size={14} className="text-gray-400 flex-shrink-0" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Type a name, room, or phone…"
            className="flex-1 text-sm bg-transparent border-0 outline-none placeholder:text-gray-400"
            data-testid={`${testId}-input`}
          />
        </div>
        <div className="max-h-72 overflow-y-auto py-1" data-testid={`${testId}-list`}>
          {filtered.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">
              {loading ? 'Loading tenants…' : tenants.length === 0 ? 'No tenants yet.' : 'No tenant matches your search.'}
            </p>
          ) : (
            filtered.map((t) => {
              const room = t.room_number && t.room_number !== 'N/A' ? t.room_number : '';
              const selectedNow = value === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => {
                    onChange(t.id, t);
                    setOpen(false);
                    setQuery('');
                  }}
                  data-testid={`${testId}-option-${t.id}`}
                  className={cn(
                    'w-full flex items-center gap-2 px-3 py-2 text-left text-sm',
                    'hover:bg-blue-50 focus:bg-blue-50 focus:outline-none transition-colors',
                    selectedNow && 'bg-blue-50',
                  )}
                >
                  <span className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[11px] font-semibold flex-shrink-0">
                    {(t.name || '?').charAt(0).toUpperCase()}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 truncate">{t.name}</p>
                    <p className="text-[11px] text-gray-500 truncate">
                      {room ? <>Room {room}</> : <span className="italic text-gray-400">no room</span>}
                      {t.phone && <> · {t.phone}</>}
                    </p>
                  </div>
                  {t.rent_amount ? (
                    <span className="text-xs text-gray-500 flex-shrink-0">
                      ₹{Number(t.rent_amount).toLocaleString()}
                    </span>
                  ) : null}
                  <Check
                    size={14}
                    className={cn(
                      'ml-1 flex-shrink-0 transition-opacity',
                      selectedNow ? 'opacity-100 text-blue-600' : 'opacity-0',
                    )}
                  />
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default TenantCombobox;
